import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/testIds";
import { fmtMonthPeriod } from "@/lib/utils_dates";
import { Plus, Search, Download, ArrowUpDown, AlertTriangle } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All", testId: TID.filterAll },
  { key: "active", label: "Active", testId: TID.filterActive },
  { key: "expiring", label: "Expiring", testId: TID.filterExpiring },
  { key: "expired", label: "Expired", testId: TID.filterExpired },
];

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportStudentsCSV(rows) {
  const headers = [
    "Name",
    "Age",
    "Phone",
    "Status",
    "Coach",
    "Start Date",
    "End Date",
    "Days Left",
    "Amount Received",
    "Pending Fee",
    "Next Due Date",
    "Received Date",
    "Comments",
  ];
  const lines = [headers.join(",")];
  for (const s of rows) {
    lines.push(
      [
        s.name,
        s.age,
        s.phone_number,
        s.status,
        s.coach_name || "",
        s.start_date,
        s.end_date,
        s.days_left,
        s.amount_received || 0,
        s.pending_fee || 0,
        s.next_due_date || "",
        s.received_date || "",
        s.comments || "",
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  const csv = "\uFEFF" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `pathaans-students-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function StudentsPage() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [coachesCanAdd, setCoachesCanAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sortByCoach, setSortByCoach] = useState(false);
  const [renewalFilter, setRenewalFilter] = useState("all"); // all | this_month | next_month

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, cfg] = await Promise.all([api.listStudents(), api.getSettings()]);
      setStudents(s);
      setCoachesCanAdd(cfg.coaches_can_add_students);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canAdd = user?.role === "admin" || coachesCanAdd;

  // Precompute duplicate name/phone occurrences (case-insensitive; digits-only phone)
  const dupIds = useMemo(() => {
    const nameCount = new Map();
    const phoneCount = new Map();
    for (const s of students) {
      const n = (s.name || "").trim().toLowerCase();
      const p = String(s.phone_number || "").replace(/\D/g, "");
      if (n) nameCount.set(n, (nameCount.get(n) || 0) + 1);
      if (p) phoneCount.set(p, (phoneCount.get(p) || 0) + 1);
    }
    const flagged = new Set();
    for (const s of students) {
      const n = (s.name || "").trim().toLowerCase();
      const p = String(s.phone_number || "").replace(/\D/g, "");
      if ((n && nameCount.get(n) > 1) || (p && phoneCount.get(p) > 1)) {
        flagged.add(s.id);
      }
    }
    return flagged;
  }, [students]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    // Compute this-month & next-month YYYY-MM prefixes for renewal filter
    const now = new Date();
    const ym = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
    const thisYM = ym(now.getFullYear(), now.getMonth());
    const nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextYM = ym(nextDate.getFullYear(), nextDate.getMonth());
    let list = students
      .filter((s) => (filter === "all" ? true : s.status === filter))
      .filter((s) => {
        if (renewalFilter === "this_month") return (s.end_date || "").startsWith(thisYM);
        if (renewalFilter === "next_month") return (s.end_date || "").startsWith(nextYM);
        return true;
      })
      .filter((s) =>
        query
          ? s.name.toLowerCase().includes(query) ||
            s.phone_number.toLowerCase().includes(query)
          : true
      );
    if (sortByCoach) {
      list = [...list].sort((a, b) => {
        const coachCmp = (a.coach_name || "~").localeCompare(
          b.coach_name || "~",
          undefined,
          { sensitivity: "base" }
        );
        if (coachCmp !== 0) return coachCmp;
        // Same coach → sort students alphabetically by name
        return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      });
    }
    return list;
  }, [students, filter, q, sortByCoach, renewalFilter]);

  return (
    <div className="space-y-6 relative">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Students</h1>
          <p className="text-slate-500 text-sm mt-1">
            {students.length} student{students.length === 1 ? "" : "s"} on roster
          </p>
        </div>
        <button
          type="button"
          data-testid="export-students-btn"
          onClick={() => exportStudentsCSV(filtered)}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-semibold"
          title="Export current view to Excel/CSV"
        >
          <Download className="w-4 h-4" />
          Export to Excel
        </button>
      </div>

      <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 h-11 bg-slate-50 focus-within:bg-white focus-within:border-[#0A2540]">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          data-testid={TID.studentSearch}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or phone"
          className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
        />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={f.testId}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              filter === f.key
                ? "bg-[#0A2540] text-white border-[#0A2540]"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          data-testid="sort-by-coach-btn"
          type="button"
          onClick={() => setSortByCoach((v) => !v)}
          className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
            sortByCoach
              ? "bg-[#0A2540] text-white border-[#0A2540]"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
          title="Toggle alphabetical sort by coach"
        >
          <ArrowUpDown className="w-3.5 h-3.5" />
          Sort by Coach
        </button>
        <select
          data-testid="renewal-filter"
          value={renewalFilter}
          onChange={(e) => setRenewalFilter(e.target.value)}
          className="h-[30px] px-2.5 rounded-full text-xs font-bold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 outline-none focus:border-[#0A2540]"
          title="Filter by renewal window"
        >
          <option value="all">All</option>
          <option value="this_month">Renewing This Month</option>
          <option value="next_month">Renewing Next Month</option>
        </select>
      </div>

      <div data-testid={TID.studentsList} className="space-y-2">
        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-slate-500 py-16 bg-slate-50 rounded-xl border border-slate-100">
            No students match your filter.
          </div>
        ) : (
          filtered.map((s, i) => <StudentRow key={s.id} s={s} dup={dupIds.has(s.id)} serial={i + 1} />)
        )}
      </div>

      {canAdd && (
        <Link
          to="/students/new"
          data-testid={TID.studentsFabAdd}
          className="fixed bottom-24 md:bottom-8 right-6 md:right-8 z-30 w-14 h-14 rounded-full bg-[#0A2540] hover:bg-[#081D33] text-white flex items-center justify-center shadow-lg"
          title="Add student"
        >
          <Plus className="w-6 h-6" />
        </Link>
      )}
    </div>
  );
}

const STATUS_STYLE = {
  active: { bg: "#34C75922", text: "#0A8F3D", label: "Active" },
  expiring: { bg: "#FF950022", text: "#B36400", label: "Expiring" },
  expired: { bg: "#FF3B3022", text: "#B4262B", label: "Expired" },
};

function StudentRow({ s, dup, serial }) {
  const style = STATUS_STYLE[s.status] || STATUS_STYLE.active;
  const period = fmtMonthPeriod(s.start_date, s.end_date);
  return (
    <Link
      to={`/students/${s.id}`}
      data-testid={TID.studentRow(s.id)}
      className="flex items-center gap-3 bg-white hover:bg-slate-50 rounded-xl p-3 border border-slate-200 transition-colors"
    >
      <span className="text-xs font-bold text-slate-400 tabular-nums w-8 text-center shrink-0">
        #{serial}
      </span>
      <div className="w-11 h-11 rounded-full bg-[#0A2540] text-white flex items-center justify-center font-bold shrink-0">
        {s.name?.charAt(0)?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-slate-900 truncate">{s.name}</p>
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
            style={{ background: style.bg, color: style.text }}
          >
            {style.label}
            {period ? ` · ${period}` : ""}
          </span>
          {dup && (
            <span
              data-testid={`dup-flag-${s.id}`}
              title="Same name or phone appears on another student"
              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider bg-[#FF950022] text-[#B36400]"
            >
              <AlertTriangle className="w-3 h-3" />
              Seems Duplicate
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 truncate">
          {s.phone_number} · Age {s.age} · Ends {s.end_date}
          {s.days_left >= 0 ? ` (${s.days_left}d)` : ` (${Math.abs(s.days_left)}d ago)`}
        </p>
        {s.coach_name && (
          <p className="text-xs font-semibold text-[#FF6B6B] mt-0.5 truncate">
            Coach: {s.coach_name}
          </p>
        )}
      </div>
    </Link>
  );
}
